import { Component, OnInit, Input } from '@angular/core';
import { InfractionService } from '@/services/infraction/infraction.service';
import { SanctionType, SanctionRecommendationWithDates } from '@/interfaces/infractions';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { dateMustBeAfterOrEqualThanToday } from '@/validators/dateMustBeAfterOrEqualThanToday';
import { dateEndSettedOrUndefined } from '@/validators/dateEndSettedOrUndefined';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { normalize } from '@/utils/normalizeString';

@Component({
  selector: 'app-form-type-sanction',
  templateUrl: './form-type-sanction.component.html',
  styleUrls: ['./form-type-sanction.component.css'],
})
export class FormTypeSanctionComponent implements OnInit {
  @Input() formNumber: number = 4;
  @Input() controlForm: FormGroup = new FormGroup({
    sanctionTypeId: new FormControl('', [Validators.required, Validators.min(1)]),
    suspensionDays: new FormControl('', [Validators.required]),
    sanctionRange: new FormGroup({
      start: new FormControl(null, [dateEndSettedOrUndefined()]),
      end: new FormControl(null, [dateEndSettedOrUndefined(), dateMustBeAfterOrEqualThanToday()]),
    }, [dateEndMustBeAfterDateStart('start', 'end')]),
    expirationDate: new FormControl(null, [dateEndSettedOrUndefined()]),
  });

  defaultDateSetted = false;
  defaultDateStart: Date = new Date();
  defaultDateEnd?: Date;

  @Input() severityName: string = '';
  @Input() infrigementName: string = '';
  @Input()
  get recommendation(): SanctionRecommendationWithDates | undefined {
    return this._recommendation;
  }
  set recommendation(recommendation: SanctionRecommendationWithDates | undefined) {
    if (recommendation) {
      const { sanctionTypeId, days, dateStart, dateEnd } = recommendation;
      this.controlForm.controls.sanctionTypeId.patchValue(sanctionTypeId);
      if (days) {
        this.controlForm.controls.suspensionDays.patchValue(days);
      }
      if (dateStart) {
        this.controlSanctionRangeStart.setValidators([dateEndSettedOrUndefined()]);
        this.controlSanctionRangeEnd.setValidators([dateEndSettedOrUndefined(), dateMustBeAfterOrEqualThanToday()]);
        this.defaultDateEnd = dateEnd;
        this.defaultDateStart = dateStart;
        this.defaultDateSetted = true;
      }
    }
    this._recommendation = recommendation;
  }
  private _recommendation?: SanctionRecommendationWithDates;

  // Escucha los cambios en assetId
  @Input()
  get assetId(): number {
    return this._assetId;
  }
  set assetId(assetId: number) {
    if (assetId) {
      this._assetId = assetId;
      this.getSanctionTypes(assetId);
      this.infractionService.getSanctionRulesMatrix(assetId).subscribe({
        next: (val) => {
          this.sanctionRulesMatrix = val;
        },
      });
    }
    this.resetForm();
  }
  private _assetId: number = 0;
  // ------------------

  get controlSanctionRangeStart(): FormControl {
    const sanctionRangeForm = this.controlForm.controls.sanctionRange as FormGroup;
    return sanctionRangeForm.controls.start as FormControl;
  }
  get controlSanctionRangeStartError(): string {
    const sanctionRangeForm = this.controlForm.controls.sanctionRange as FormGroup;
    const error = sanctionRangeForm.controls.start.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  get controlSanctionRangeEnd(): FormControl {
    const sanctionRangeForm = this.controlForm.controls.sanctionRange as FormGroup;
    return sanctionRangeForm.controls.end as FormControl;
  }

  get controlSanctionRangeEndError(): string {
    const sanctionRangeForm = this.controlForm.controls.sanctionRange as FormGroup;
    const errorEnd = sanctionRangeForm.controls.end.errors;
    const errorRange = sanctionRangeForm.errors;

    const currentError = errorEnd || errorRange;

    if (currentError) {
      if (currentError.description) {
        return currentError.description;
      }
    }
    return '';
  }

  get controlExpirationDate(): FormControl {
    return this.controlForm.controls.expirationDate as FormControl;
  }

  get controlExpirationDateError(): string {
    const error = this.controlForm.controls.expirationDate.errors;

    if (error) {
      if (error.description) {
        return error.description;
      }
    }

    return '';
  }

  constructor(
    private infractionService: InfractionService,
  ) { }

  shouldShowSanctionTypeModal: boolean = false;
  sanctionTypes: SanctionType[] = [];
  sanctionRulesMatrix: string[][] = [];

  ngOnInit(): void {
    this.controlForm.controls.sanctionTypeId.valueChanges.subscribe({
      next: (values) => {
        if (this.sanctionIsThis('amonestacion')) {
          this.controlForm.controls.suspensionDays.patchValue(-1);

          this.controlForm.controls.sanctionRange.reset();
          (this.controlForm.controls.sanctionRange as FormGroup).controls.start.patchValue(undefined);
          (this.controlForm.controls.sanctionRange as FormGroup).controls.end.patchValue(undefined);

          this.controlForm.controls.expirationDate.reset();
          this.controlForm.controls.expirationDate.patchValue(undefined);
        }
        else if (this.sanctionIsThis('suspensión')) {
          this.controlForm.controls.suspensionDays.patchValue('');
          this.controlForm.controls.sanctionRange.reset();
          this.controlForm.controls.expirationDate.reset();
          this.controlForm.controls.expirationDate.patchValue(undefined);
        }
        else if (this.sanctionIsThis('caducacion')) {
          this.controlForm.controls.expirationDate.reset();

          this.controlForm.controls.suspensionDays.patchValue(-1);

          this.controlForm.controls.sanctionRange.reset();
          (this.controlForm.controls.sanctionRange as FormGroup).controls.start.patchValue(undefined);
          (this.controlForm.controls.sanctionRange as FormGroup).controls.end.patchValue(undefined);
        }
      },
    });
  }

  getSanctionTypes(assetId: number) {
    this.infractionService.getSanctionTypes(assetId).subscribe({
      next: (sanctionTypes) => {
        this.sanctionTypes = sanctionTypes;
      },
    });
  }

  sanctionIsThis(sanctionName: string) {
    const selectedSanctionId = this.controlForm.controls.sanctionTypeId.value;
    const selectedSanction = this.sanctionTypes.find((sanction) => +selectedSanctionId === sanction.id);
    if (selectedSanction) {
      return normalize(selectedSanction.name) === normalize(sanctionName);
    }
    return false;
  }

  showSanctionTypeModal() {
    this.shouldShowSanctionTypeModal = true;
  }

  closeSanctionTypeModal() {
    this.shouldShowSanctionTypeModal = false;
  }

  resetForm() {
    this.controlForm.patchValue({
      sanctionTypeId: '',
      suspensionDays: '',
      sanctionRange: { start: null, end: null },
      expirationDate: null,
    });
  }

}
